import { API_BASE_URL } from "@/lib/config/site";
import type { Facets, ProductDetail, ProductPage, SearchPage } from "./types";

/**
 * Catalog + search client.
 *
 * Everything runs from the browser: the catalog is 30k rows behind filters a
 * visitor changes constantly, and the Capacitor build has no server to fetch
 * from at all. Rendering it server-side would either bake stale results into
 * static HTML or make the pages dynamic, which the export build forbids.
 * The shells around it (headings, breadcrumb, metadata) stay server-rendered,
 * so crawlers still get a real localized page.
 */

export class CatalogError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message);
    this.name = "CatalogError";
  }
}

export interface CatalogFilters {
  section?: string;
  /** Category slugs — repeatable; the backend ANDs them with the section. */
  category?: string[];
  brand?: string[];
  supplier?: string[];
  priceMin?: number;
  priceMax?: number;
  inStock?: boolean;
  minRating?: number;
  currency?: string;
}

function params(filters: CatalogFilters): URLSearchParams {
  const p = new URLSearchParams();
  if (filters.section) p.set("section", filters.section);
  for (const c of filters.category ?? []) p.append("category", c);
  for (const b of filters.brand ?? []) p.append("brand", b);
  for (const s of filters.supplier ?? []) p.append("supplier", s);
  if (filters.priceMin != null) p.set("price_min", String(filters.priceMin));
  if (filters.priceMax != null) p.set("price_max", String(filters.priceMax));
  if (filters.inStock) p.set("in_stock", "true");
  if (filters.minRating != null) p.set("min_rating", String(filters.minRating));
  if (filters.currency) p.set("currency", filters.currency);
  return p;
}

async function get<T>(path: string, query: URLSearchParams, signal?: AbortSignal): Promise<T> {
  const url = `${API_BASE_URL}${path}?${query.toString()}`;
  const res = await fetch(url, { signal, headers: { Accept: "application/json" } });
  if (!res.ok) throw new CatalogError(`${path} responded ${res.status}`, res.status);
  return (await res.json()) as T;
}

/** One product. Runs on the server for the product page, unlike the rest of this file. */
export async function getProduct(
  slug: string,
  opts: { revalidate?: number } = {},
): Promise<ProductDetail> {
  const path = `/catalog/products/${encodeURIComponent(slug)}`;
  const res = await fetch(`${API_BASE_URL}${path}`, {
    headers: { Accept: "application/json" },
    next: opts.revalidate != null ? { revalidate: opts.revalidate } : undefined,
  });
  if (!res.ok) throw new CatalogError(`${path} responded ${res.status}`, res.status);
  return (await res.json()) as ProductDetail;
}

/** Absolute URL of the backend redirect to an outlet. */
export function outletHref(outPath: string): string {
  return `${API_BASE_URL}${outPath}`;
}

/** Hybrid search (pgvector + full-text, fused with RRF) — offset paginated. */
export function searchProducts(
  query: string,
  filters: CatalogFilters = {},
  opts: { limit?: number; offset?: number; signal?: AbortSignal } = {},
): Promise<SearchPage> {
  const p = params(filters);
  p.set("q", query);
  p.set("limit", String(opts.limit ?? 24));
  p.set("offset", String(opts.offset ?? 0));
  return get<SearchPage>("/search", p, opts.signal);
}

/**
 * Browse a section/category — keyset paginated.
 *
 * Deliberately a different pagination scheme from search: the scraper inserts
 * rows while a visitor scrolls, and with OFFSET page 2 would repeat rows from
 * page 1 (docs/PROJECT.md). Search can afford offsets because its ordering is
 * by relevance against a fixed query.
 */
export function listProducts(
  filters: CatalogFilters = {},
  opts: { sort?: string; cursor?: string | null; limit?: number; signal?: AbortSignal } = {},
): Promise<ProductPage> {
  const p = params(filters);
  if (opts.sort) p.set("sort", opts.sort);
  if (opts.cursor) p.set("cursor", opts.cursor);
  p.set("limit", String(opts.limit ?? 24));
  return get<ProductPage>("/catalog/products", p, opts.signal);
}

/** Filter counts for the current selection (drives the sidebar). */
export function getFacets(
  filters: CatalogFilters = {},
  signal?: AbortSignal,
): Promise<Facets> {
  return get<Facets>("/catalog/facets", params(filters), signal);
}
